import {
    Button,
    DateTimeInput,
    Dialog,
    DialogDescription,
    DialogHeader,
    Grid,
    GridRow,
    Icon,
    Input,
    InputProps,
    Select,
    SelectProps,
    ToastLevel,
    useIntl,
    useToast,
} from "@metsooutotec/modes-react-components"
import { CreateProvisionEvent, EditProvisionEvent, ProvisionEventEquipment, ProvisionType } from "features/provisioning/provisions/models/provision-models"
import { useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import provisionEventStore from "features/provisioning/provisions/stores/provision-event-store"
import { validateText } from "validators/inputValidators"
import { provisionEventsLinkGeneratorSimple } from "../../provisions-utils/provision-events-link-generator"
import { convertDateToDDMMMYY, convertDateToDDMMMYYYY, convertDDMMMYYtoDate, getTodayDateString } from "components/utils/date-formatter-utils"

export type ProvisionEventModalOptions = {
    created: boolean
    edited: boolean
}

type ProvisionEventModalProps = {
    isOpen: boolean | undefined
    onClose: () => void
    provisionEvent?: ProvisionEventEquipment
    customerId: string
    siteId: string
    assetId: string
    assetSectionId: string
}

type ProvisionEventModalForm = {
    description: string
    type: string
}

//TODO: Move to css module
const cssStyle = `
.date-time-adjust > div {
  width: 100%;
}
.date-time-adjust > div > div {
  width: 100%;
}
.min-height-adjust-1 > div > div > div {
  min-height: 48px;
  align-items: center;
}
.min-height-adjust-2 > div > div > div {
  min-height: 41px;
}
`

export const ProvisionEventModal = (props: ProvisionEventModalProps) => {
    const { customerId, siteId, assetId, assetSectionId, provisionEvent, isOpen, onClose } = props
    const isEditing = !!provisionEvent
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { setMessage } = useToast()
    const [isLoading, setLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<{ isError: boolean; errorMessage: string }>({ isError: false, errorMessage: "" })
    const [eventDate, setEventDate] = useState<string>(convertDateToDDMMMYY(isEditing ? provisionEvent?.eventDate : new Date()))
    const [formData, setFormData] = useState<ProvisionEventModalForm>({
        description: provisionEvent?.description || "",
        type: provisionEvent?.type || "Planned",
    })
    const [formErrors, setFormErrors] = useState<Partial<ProvisionEventModalForm>>({
        description: "",
        type: "",
    })
    const [datePickerError, setDatePickerError] = useState<{ isError: boolean; errorMessage: string }>({ isError: false, errorMessage: "" })
    const { saveProvisionEvent, createProvisionEvent } = provisionEventStore

    const labels = {
        confirm: formatMessage("modal.confirm"),
        cancel: formatMessage("modal.cancel"),
        header: isEditing ? formatMessage("provisionEvents.modal.edit.header") : formatMessage("provisionEvents.modal.create.header"),
        description: formatMessage("provisionEvents.modal.description"),
        descriptionData: formatMessage("provisionEvents.modal.data.description"),
        eventDate: formatMessage("provisionEvents.modal.data.eventDate"),
        type: formatMessage("provisionEvents.modal.data.type"),
    }

    const handleChange = (fieldName: string, value: string) => {
        isError.isError = false
        setFormData(prevData => ({
            ...prevData,
            [fieldName]: value,
        }))
    }
    const handleValidation = (fieldName: string, validationError: string | any): void => {
        setFormErrors(prevErrors => ({
            ...prevErrors,
            [fieldName]: validationError,
        }))
    }

    const onCloseProcess = (): void => {
        resetForm()
        setIsError({ isError: false, errorMessage: "" })
        onClose()
    }

    const resetForm = (): void => {
        resetFormData()
        resetErrors()
    }

    const resetErrors = (): void => {
        setFormErrors({
            description: "",
            type: "",
        })
        setDatePickerError({ isError: false, errorMessage: "" })
    }

    const resetFormData = () => {
        setFormData({
            description: "",
            type: "",
        })
        setEventDate(getTodayDateString)
    }

    const onClearClick = (inputId: string) => {
        setFormData(prevData => ({
            ...prevData,
            [inputId]: "",
        }))
        setFormErrors(prevErrors => ({
            ...prevErrors,
            [inputId]: "",
        }))
    }

    const handleDateChange = useCallback((value: string) => {
        try {
            setDatePickerError({ isError: false, errorMessage: "" })
            const date = new Date(value)
            if (isNaN(date.getDate())) {
                setDatePickerError({ isError: true, errorMessage: "Invalid Date!" })
            } else {
                const formatedDate = convertDateToDDMMMYY(date)
                setEventDate(formatedDate)
            }
        } catch (e) {
            console.log(e)
            setDatePickerError({ isError: true, errorMessage: (e as any).message })
        }
    }, [])

    const validateAssetSectionForm = (formData: ProvisionEventModalForm): Partial<ProvisionEventModalForm> => {
        const errors: Partial<ProvisionEventModalForm> = {}
        errors.description = validateText(formData.description)
        errors.type = validateText(formData.type)
        return errors
    }

    const handleSubmit = useCallback(
        async (e: React.FormEvent): Promise<void> => {
            e.preventDefault()
            try {
                setLoading(true)

                // Validate the form
                const errors: Partial<ProvisionEventModalForm> = validateAssetSectionForm(formData)
                const hasErrors = Object.values(errors).some(error => error !== "")

                if (hasErrors) {
                    setIsError({ isError: true, errorMessage: "There are validation errors, update the form texts" })
                    setFormErrors(errors)
                    setLoading(false) // Reset loading state
                    return
                }

                const { description, type } = formData
                const date = convertDDMMMYYtoDate(eventDate)
                if (!date) {
                    setIsError({ isError: true, errorMessage: "Invalid date" })
                    setFormErrors(errors)
                    return
                }

                const data: CreateProvisionEvent = {
                    assetId: assetId || "",
                    description: description || "",
                    eventDate: convertDateToDDMMMYYYY(date),
                    type: type as ProvisionType,
                }

                const whenEditing = async () => {
                    if (!provisionEvent) {
                        return
                    }

                    var eq: EditProvisionEvent = {
                        id: provisionEvent.id,
                        assetId: provisionEvent.assetId,
                        description: data.description,
                        eventDate: convertDateToDDMMMYYYY(date),
                        type: data.type as ProvisionType,
                        assetSectionId: assetSectionId,
                    }

                    await saveProvisionEvent(eq)
                    const link = provisionEventsLinkGeneratorSimple(customerId, siteId, assetId, assetSectionId, provisionEvent.id) + "/edit"
                    return link
                }

                const whenCreating = async () => {
                    var provisionEventDetail = await createProvisionEvent(data, date)
                    const link = provisionEventsLinkGeneratorSimple(customerId, siteId, assetId, assetSectionId, provisionEventDetail.id) + "/edit"
                    return link
                }

                const link = await (isEditing ? whenEditing : whenCreating)()
                if (link) navigate(link)
            } catch (error) {
                setIsError({ isError: true, errorMessage: "Form submission error, please read logs" })
                // TODO i18n
                setMessage("Failed to save", ToastLevel.ALERT, 6000)
                console.error("Form submission error:", error)
            } finally {
                setLoading(false)
            }
        },
        [provisionEvent, formData, eventDate],
    )

    if (!isOpen) {
        return
    }
    const renderInputs = (): React.ReactNode => {
        const inputProps: InputProps = {
            id: "description",
            label: labels.descriptionData,
            placeholder: `Type ${labels.descriptionData} here...`,
            validate: validateText,
            onValidation: (validationError: string | undefined) => handleValidation("description", validationError),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange("description", e.target.value),
            type: "string",
            error: formErrors.description,
            spanning: true,
            heightClass: "medium",
            required: true,
            uiRequired: true,
            value: formData.description,
            icon: formData.description && <Icon className='icon-cross-circle-fill-16' onClick={() => onClearClick("description")} />,
        }

        const selectInputProps: SelectProps = {
            id: "type",
            label: labels.type,
            placeholder: ``,
            validate: validateText,
            onValidation: (validationError: string | undefined) => handleValidation("type", validationError),
            onChange: (value: string | string[]) => handleChange("type", Array.isArray(value) ? value[0] : value),
            type: "select",
            error: formErrors.type,
            heightClass: "small",
            spanning: true,
            required: true,
            uiRequired: true,
            icon: null,
            value: formData.type,
            options: [
                {
                    text: "Planned",
                    value: ProvisionType.Planned,
                },
                {
                    text: "Commissioned",
                    value: ProvisionType.Commissioned,
                },
                {
                    text: "Decommissioned",
                    value: ProvisionType.Decommissioned,
                },
            ],
            freeText: false,
            multiSelect: false,
        }

        return (
            <>
                <style>{cssStyle}</style>
                <GridRow style={{ marginBottom: "var(--size-m)" }}>
                    <Input {...inputProps} />
                </GridRow>
                <GridRow className='date-time-adjust min-height-adjust-1' style={{ marginBottom: "var(--size-m)" }}>
                    <DateTimeInput
                        label={labels.eventDate}
                        onChange={(value: string | any) => handleDateChange(value.toString())}
                        value={convertDDMMMYYtoDate(eventDate) as unknown as string}
                        format='D-MMM-YY'
                        timeFormat={false}
                        error={datePickerError.errorMessage}
                    />
                </GridRow>
                <GridRow className='min-height-adjust-2' style={{ marginBottom: "var(--size-m)" }}>
                    <Select {...selectInputProps} />
                </GridRow>
            </>
        )
    }

    const renderButtons = (): React.ReactNode => {
        const buttonsConfig = [
            { label: labels.confirm, onClick: handleSubmit, primary: true },
            { label: labels.cancel, onClick: onCloseProcess, primary: false },
        ]
        return buttonsConfig.map((button, index) => {
            const { label, onClick, primary } = button
            const buttonGridStyle =
                index === 0
                    ? {
                          marginBottom: "var(--size-m)",
                      }
                    : {}
            return (
                <GridRow key={label} style={buttonGridStyle}>
                    <Button
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={onClick}
                        flexible={true}
                        children={label}
                        centered
                        size={"medium"}
                        primary={primary}
                    />
                </GridRow>
            )
        })
    }

    return (
        <Grid>
            <Dialog dimmer open={isOpen} size={"small"} style={{ maxWidth: "512px" }}>
                <DialogHeader username={""}>{labels.header}</DialogHeader>
                {isError.isError && <p style={{ color: "red" }}>{isError.errorMessage}</p>}
                <DialogDescription style={{ marginBottom: "var(--size-xxl)" }}>{labels.description}</DialogDescription>
                {renderInputs()}
                <div style={{ marginBottom: "var(--size-xxl)" }} />
                {renderButtons()}
            </Dialog>
        </Grid>
    )
}
